import type { z } from 'zod'

/**
 * O cliente HTTP do mundo, contra o `api`. Mesmo transporte do `web2` (ADR 0011): origem cruzada, cookie de
 * renovacao com `credentials: 'include'`, access token no header.
 *
 * Devolve resultado, e nunca lanca: rede caida e status 0, como no `web2`, e a tela decide o que mostrar.
 */

export type ApiResult<T> = { ok: true; data: T } | { ok: false; status: number; code: string }

export type ApiClient = {
  request<T>(
    path: string,
    schema: z.ZodType<T>,
    init?: { method?: string; body?: unknown; keepalive?: boolean },
  ): Promise<ApiResult<T>>
}

export function createApiClient(
  baseUrl: string,
  accessToken: () => string | null,
  fetcher: typeof fetch = (...args) => fetch(...args),
): ApiClient {
  return {
    async request(path, schema, init = {}) {
      const token = accessToken()
      let response: Response
      try {
        response = await fetcher(`${baseUrl}${path}`, {
          method: init.method ?? 'GET',
          credentials: 'include',
          headers: {
            accept: 'application/json',
            ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
            ...(token === null ? {} : { authorization: `Bearer ${token}` }),
          },
          ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
          // A saida do mundo sai junto com a navegacao: `keepalive` deixa o pedido terminar depois que a pagina
          // ja foi embora.
          ...(init.keepalive === true ? { keepalive: true } : {}),
        })
      } catch {
        return { ok: false, status: 0, code: 'NETWORK' }
      }

      const payload: unknown = response.status === 204 ? null : await response.json().catch(() => null)
      if (!response.ok) {
        const code =
          typeof payload === 'object' && payload !== null && 'code' in payload ? String(payload.code) : 'HTTP_ERROR'
        return { ok: false, status: response.status, code }
      }
      const parsed = schema.safeParse(payload)
      return parsed.success
        ? { ok: true, data: parsed.data }
        : { ok: false, status: response.status, code: 'BAD_RESPONSE' }
    },
  }
}
